import {getTestProps, DataTestAttrs} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import $ from 'jquery';
import PropTypes from 'prop-types';
import React, {CSSProperties, ReactElement} from 'react';
import ReactDOM from 'react-dom';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import {autoFocusFirstOption, handleMenuNavigation} from 'lib/a11y/MenuKeyboardNavigation';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import throttle from 'util/throttle';

// Used to distinguish the component class when rendering; is always set as default props by Menu children.
enum Role {
  MENU = 'menu',
  OPTION = 'option',
  SECTION = 'section',
  SECTION_HEADER = 'sectionHeader',
  FOOTER = 'footer',
  FOOTER_OPTION = 'footerOption',
  WRAPPER = 'wrapper',
}

export const ROLES = Role;

export const WINDOW_PADDING = 10;
export const MIN_DROP_UNDER_HEIGHT = 100;

const DEFAULT_SELECTED_ICON = 'check';

type MenuValue = boolean | string | number | object;

export interface MenuProps {
  style?: CSSProperties;
  children?: React.ReactNode;
  className?: string;
  // Overrides the default `{component: 'NestedMenu', element: 'root'}`
  dataTestAttrs?: DataTestAttrs;
  // whether or not to make the dropdown menu scrollable
  scrollable: boolean;
  // Props for scrollable
  scrollableClassName?: string;
  // The currently selected `value`; any options that have the same value will be marked as selected.
  value?: MenuValue | Array<MenuValue> | null;
  // Custom function for testing value equality; if not specified, isEqual will be used by default.
  // It is possible for a menu option to not have a value, so make sure undefined and null are supported.
  isValueEqual?: (firstValue?: MenuValue | null, secondValue?: MenuValue | null) => boolean;

  // The following props are applied on both the top-level menu and any submenus
  // The height for each item in the dropdown menu (see the guidelines).
  height?: MenuHeight;
  // The width for the dropdown menu (see the guidelines).
  width?: MenuWidth;
  // If `dropLeft`, menu and submenus will drop to the left.
  dropLeft?: boolean;
  // If `dropUp`, menu and submenus will drop up, even if they have enough space to drop down.
  dropUp?: boolean;
  isMulti?: boolean;
  // If `useSelectedIconIfSingleSelect`, use a default icon for selected options instead of its `icon`
  useSelectedIconIfSingleSelect?: boolean;
  onMouseLeave?: () => void;
  footer?: JSX.Element;
  /**
   * If the current Menu is a submenu rendered within a Float, this prop is used to
   * calculate the maxHeight. This is necessary because the parent element for a Float
   * is the HTML body, which is not useful for maxHeight calculation.
   */
  parentMenuOptionContainer?: HTMLElement | null;
  /**
   * Disables keyboard navigation and auto-focusing of menu options.
   * Use when the parent component handles keyboard input (e.g., with a text input).
   * Defaults to false (keyboard access enabled).
   */
  disableKeyboardAccess?: boolean;
  // Do not define `_role` -- this is used internally by the component.
  _role: Role;
  'aria-label'?: string;
  // Callback to close the dropdown (used for Escape key)
  onClose?: () => void;
}

interface SharedChildProps {
  isSelected: boolean;
  iconClassName?: string;
  iconWrapperClassName?: string;
  icon?: BenchlingIconName;
}

interface ChildProps {
  value: MenuValue;
  isSelected: boolean;
}

interface State {
  dropup: boolean;
  maxHeight: number | null;
  scrollable: boolean;
}

// Exported for testing.
export class NestedMenu extends React.Component<MenuProps, State> {
  override state: State = {
    dropup: this.props.dropUp ?? false,
    maxHeight: null,
    scrollable: false,
  };

  private optionRefs: Array<React.RefObject<HTMLElement>> = [];
  private optionRefIndex = 0;
  private previouslyFocusedElement: HTMLElement | null = null;

  _isSelected(childProps: ChildProps): boolean {
    const isEqualFn = this.props.isValueEqual || isEqual;
    if (this.props.isMulti) {
      return (
        Array.isArray(this.props.value) &&
        this.props.value.some((value: MenuValue) => isEqualFn(value, childProps.value))
      );
    }
    return (
      (childProps.value !== undefined && isEqualFn(childProps.value, this.props.value)) ||
      childProps.isSelected
    );
  }

  _isChildEmpty(child?: JSX.Element): boolean {
    if (!child) {
      return true;
    }
    // Only render a section if it has non-hidden options.
    if (child.props._role === Role.SECTION) {
      return !React.Children.toArray(child.props.children).some(
        (node: ReactElement<MenuProps>) => node && node.props && node.props._role === Role.OPTION
      );
    }
    return false;
  }

  _hasChildSubmenu(child?: JSX.Element): boolean {
    return (
      child != null &&
      React.Children.toArray(child.props.children).some(
        (node: ReactElement<MenuProps>) => node && node.props && node.props._role === Role.MENU
      )
    );
  }

  _getSharedChildProps(child: JSX.Element): SharedChildProps {
    const isSelected = this._isSelected(child.props);
    const overriddenProps: SharedChildProps = {isSelected};
    if (isSelected) {
      overriddenProps.iconClassName = classNames(child.props.iconClassName, 'is-selected');
    }
    // For multi-select dropdowns, always use a checkmark icon. Allow skipping it with `skipIconOverride`
    // for a placeholder option (e.g. loading, error, empty query).
    if (this.props.isMulti && !child.props.skipIconOverride) {
      if (isSelected) {
        overriddenProps.icon = DEFAULT_SELECTED_ICON;
      } else {
        overriddenProps.icon = child.props.icon || DEFAULT_SELECTED_ICON;
        overriddenProps.iconWrapperClassName = classNames(
          child.props.iconWrapperClassName,
          css(styles.hiddenIcon)
        );
      }
    } else if (isSelected && this.props.useSelectedIconIfSingleSelect) {
      overriddenProps.icon = DEFAULT_SELECTED_ICON;
    }

    return overriddenProps;
  }

  _renderChild(
    child: JSX.Element | undefined | null,
    isLastNonEmptyChild: boolean
  ): Array<JSX.Element> | JSX.Element | null {
    if (!child || this._isChildEmpty(child)) {
      return null;
    }
    switch (child.props._role) {
      case Role.SECTION: {
        const sectionChildren = this._renderChildren(child.props);
        if (!isLastNonEmptyChild) {
          sectionChildren.push(<li className="menu-divider" role="separator" />);
        }
        return sectionChildren;
      }

      case Role.SECTION_HEADER: {
        return child;
      }

      case Role.FOOTER: {
        return [
          <li className="menu-divider" key="menu-footer" role="separator" />,
          ...this._renderChildren(child.props),
        ];
      }

      case Role.FOOTER_OPTION: {
        return child;
      }

      case Role.OPTION: {
        const sharedChildProps = this._getSharedChildProps(child);
        const hasSubmenu = this._hasChildSubmenu(child);

        // Create or reuse a React ref for this option if it's not disabled
        let optionRef: React.RefObject<HTMLElement> | undefined;
        if (!child.props.isDisabled) {
          const index = this.optionRefIndex++;
          // Reuse existing ref if available, otherwise create a new one
          if (!this.optionRefs[index]) {
            this.optionRefs[index] = React.createRef<HTMLElement>();
          }
          optionRef = this.optionRefs[index];
        }

        // Render an option with a submenu.
        if (hasSubmenu) {
          assert(
            child.props.children.length === 2 && child.props.children[1].props._role === Role.MENU,
            'Option with menu must have exactly 2 children (child and menu).'
          );
          const menuProps = child.props.children[1].props;
          const menu = <NestedMenu {...this.props} {...menuProps} />;
          return React.cloneElement(
            child,
            {
              ...sharedChildProps,
              _submenu: menu,
              optionRef,
            },
            child.props.children[0]
          );
          // Render the option without a submenu.
        } else {
          return React.cloneElement(child, {...sharedChildProps, optionRef});
        }
      }

      case Role.WRAPPER: {
        return child;
      }

      default:
        assert(false, 'Unexpected menu child.');
        return null;
    }
  }

  _renderChildren(props: MenuProps, isTopLevel: boolean = false): Array<JSX.Element> {
    // Reset index only for top-level render (not for nested sections)
    if (isTopLevel) {
      this.optionRefIndex = 0;
    }

    let maxNonEmptyChildIndex = 0;
    React.Children.forEach(props.children, (child: JSX.Element, index) => {
      if (!this._isChildEmpty(child)) {
        maxNonEmptyChildIndex = index;
      }
    });
    const renderedChildren = React.Children.map(props.children, (child: JSX.Element, index: number) =>
      this._renderChild(child, index === maxNonEmptyChildIndex)
    );

    // Trim unused refs after top-level render completes
    if (isTopLevel) {
      this.optionRefs.length = this.optionRefIndex;
    }

    if (!renderedChildren) {
      return [];
    }
    return renderedChildren.flatMap((child) => child);
  }

  _renderFooter(footer: JSX.Element): JSX.Element | null {
    return this._renderChild(
      React.cloneElement(footer, this._getSharedChildProps(footer)),
      true
    ) as JSX.Element;
  }

  _renderMenu(props: MenuProps): JSX.Element {
    const children = this._renderChildren(props, true);
    const footer: JSX.Element | null = props.footer ? this._renderFooter(props.footer) : null;
    let dataTestProps = getTestProps({component: 'NestedMenu', element: 'root'});
    if (this.props.dataTestAttrs != null) {
      dataTestProps = getTestProps(this.props.dataTestAttrs);
    }
    return (
      <div
        role="menu"
        aria-label={props['aria-label']}
        tabIndex={-1}
        className={classNames('menu', `menu--${props.width}Width`, `menu--${props.height}`, props.className, {
          'menu--single': !props.isMulti,
          'menu--dropLeft': props.dropLeft,
          'menu--dropup': this.state.dropup,
          [css(styles.dropUp)]: this.state.dropup,
        })}
        style={
          this.state.dropup
            ? {
                // If this is a submenu being rendered in a `Float`, outside a `Menu` parent, align the bottom
                // of the menu with the bottom of the main menu option that 'launched' this submenu.
                bottom:
                  props.parentMenuOptionContainer != null ? -props.parentMenuOptionContainer.clientHeight : 0,
              }
            : {}
        }
        {...(this.props.disableKeyboardAccess ? {} : {onKeyDown: this._handleKeyDown})}
        {...dataTestProps}
      >
        <ul
          className={classNames(
            'menu-body',
            {
              scrollable: this.props.scrollable || this.state.scrollable,
            },
            this.props.scrollableClassName
          )}
          style={props.style}
          onMouseLeave={props.onMouseLeave}
        >
          {children}
        </ul>
        {footer && <ul className={classNames(props.className, `menu--${props.width}Width`)}>{footer}</ul>}
      </div>
    );
  }

  _resize = (): void => {
    // Making the menu scrollable blocks opening any submenus, so no-op instead
    if (
      React.Children.toArray(this.props.children).some((child: JSX.Element) => this._hasChildSubmenu(child))
    ) {
      return;
    }

    // The top of the submenu is aligned to the top of the parent, so use the parent menu to determine the
    // maxHeight, even if the submenu isn't visible yet
    const menuNode = $(ReactDOM.findDOMNode(this) as HTMLElement);
    const parent = this.props.parentMenuOptionContainer ?? menuNode.closest('li').get(0);
    if (!parent) {
      return;
    }
    const parentRect = parent.getBoundingClientRect();
    const windowHeight = $(window).height();
    const menuHeight = menuNode.get(0).scrollHeight;
    const newState = _getSubmenuPositionState(windowHeight, parentRect, menuHeight, this.props.dropUp);
    if (newState) {
      this.setState(newState);
    }
  };

  _resizeHandler = throttle(this._resize, 10);

  private _handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (this.props.disableKeyboardAccess) {
      return;
    }

    handleMenuNavigation({
      event: e,
      optionRefs: this.optionRefs,
      onClose: this.props.onClose,
    });
  };

  override componentDidMount(): void {
    window.addEventListener('resize', this._resizeHandler);
    this._resize();
    this.previouslyFocusedElement = document.activeElement as HTMLElement;
    if (!this.props.disableKeyboardAccess) {
      autoFocusFirstOption(this.optionRefs);
    }
  }

  override componentWillUnmount(): void {
    window.removeEventListener('resize', this._resizeHandler);
    // Restore focus to the previously focused element when the menu closes
    if (this.previouslyFocusedElement && !this.props.disableKeyboardAccess) {
      this.previouslyFocusedElement.focus({preventScroll: true});
    }
  }

  override render(): JSX.Element {
    return this._renderMenu({
      ...this.props,
      style: {...this.props.style, maxHeight: this.state.maxHeight ?? this.props.style?.maxHeight},
    });
  }
}

/**
 * This Menu component supports submenus and various sizes. Menu should be composed of <Option /> components
 * and optionally submenus.
 *
 * See /_design/dropdowns to see example usages.
 */
export default class Menu extends React.Component<MenuProps> {
  static HEIGHTS = MenuHeight;
  static WIDTHS = MenuWidth;

  static propTypes = {
    style: PropTypes.object,
    children: PropTypes.node,
    className: PropTypes.string,
    // The height for each item in the dropdown menu (see the guidelines).
    height: PropTypes.oneOf(Object.values(MenuHeight)),
    // The width for the dropdown menu (see the guidelines).
    width: PropTypes.oneOf(Object.values(MenuWidth)),
    scrollable: PropTypes.bool.isRequired,
    // If `dropLeft`, menu and submenus will drop to the left.
    dropLeft: PropTypes.bool,
    // If `dropUp`, menus and submenus will drop up, even if they have enough space to drop down.
    dropUp: PropTypes.bool,
    isMulti: PropTypes.bool,
    // If `useSelectedIconIfSingleSelect`, use a default icon for selected options instead of its `icon`
    useSelectedIconIfSingleSelect: PropTypes.bool,
    onMouseLeave: PropTypes.func,
    // The currently selected `value`; any options that have the same value will be marked as selected.
    value: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
      PropTypes.number,
      PropTypes.object,
      PropTypes.array,
    ]),
    parentMenuOptionContainer: PropTypes.instanceOf(HTMLElement),

    // Do not define `_role` -- this is used internally by the component.
    _role: PropTypes.oneOf(Object.values(Role)).isRequired,
  };
  static defaultProps = {
    height: MenuHeight.MEDIUM,
    scrollable: false,
    _role: Role.MENU,
  };

  override render(): JSX.Element {
    return <NestedMenu {...this.props} />;
  }
}

export function _getSubmenuPositionState(
  windowHeight: number,
  parentRect: DOMRect,
  submenuHeight: number,
  dropUp?: boolean
): State | null {
  // Calculate a maxHeight to prevent the menu from extending beyond the screen
  let maxHeight = windowHeight - parentRect.top - WINDOW_PADDING;
  let dropup = dropUp ?? false;

  // Prefer dropping down, so only drop up if menu would be too small to be useful
  if (maxHeight < MIN_DROP_UNDER_HEIGHT) {
    maxHeight = parentRect.bottom - WINDOW_PADDING;
    dropup = true;
  }
  const scrollable = submenuHeight > maxHeight;
  return {dropup, maxHeight, scrollable};
}

const styles = StyleSheet.create({
  dropUp: {
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 0,
    top: 'auto',
  },
  hiddenIcon: {
    visibility: 'hidden',
  },
});
